import { useCallback, useEffect, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { DateRange } from "rsuite/esm/DateRangePicker";

export interface ExpeditionFilters {
  search: string;
  country: string;
  userId: string;
  dateRange: DateRange | null;
  sorting: {
    field: string;
    direction: "ASC" | "DESC";
  };
}

type FilterAction =
  | { type: "SET_SEARCH"; payload: string }
  | { type: "SET_COUNTRY"; payload: string }
  | { type: "SET_USER_ID"; payload: string }
  | {
      type: "SET_DATE_RANGE";
      payload: DateRange | null;
    }
  | {
      type: "SET_SORTING";
      payload: { field: string; direction: "ASC" | "DESC" };
    }
  | { type: "RESET_FILTERS" }
  | { type: "SET_FROM_URL"; payload: Partial<ExpeditionFilters> };

const initialState: ExpeditionFilters = {
  search: "",
  country: "",
  userId: "",
  dateRange: null,
  sorting: {
    field: "createdAt",
    direction: "DESC",
  },
};

function filterReducer(state: ExpeditionFilters, action: FilterAction): ExpeditionFilters {
  switch (action.type) {
    case "SET_SEARCH":
      return { ...state, search: action.payload };
    case "SET_COUNTRY":
      return { ...state, country: action.payload };
    case "SET_USER_ID":
      return { ...state, userId: action.payload };
    case "SET_DATE_RANGE":
      return { ...state, dateRange: action.payload };
    case "SET_SORTING":
      return { ...state, sorting: action.payload };
    case "RESET_FILTERS":
      return initialState;
    case "SET_FROM_URL":
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

export const useExpeditionFilters = () => {
  const [_searchParams, setSearchParams] = useSearchParams();
  const [filters, dispatch] = useReducer(filterReducer, initialState);

  // Update URL when filters change
  const updateURL = useCallback(
    (newFilters: ExpeditionFilters) => {
      const newSearchParams = new URLSearchParams();

      if (newFilters.search) newSearchParams.set("search", newFilters.search);
      if (newFilters.country) newSearchParams.set("country", newFilters.country);
      if (newFilters.userId) newSearchParams.set("userId", newFilters.userId);
      if (newFilters.dateRange) {
        newSearchParams.set("startDate", newFilters.dateRange[0].toISOString());
        newSearchParams.set("endDate", newFilters.dateRange[1].toISOString());
      }
      if (newFilters.sorting.field !== initialState.sorting.field) {
        newSearchParams.set("sortField", newFilters.sorting.field);
      }
      if (newFilters.sorting.direction !== initialState.sorting.direction) {
        newSearchParams.set("sortDirection", newFilters.sorting.direction);
      }

      setSearchParams(newSearchParams, { replace: true });
    },
    [setSearchParams],
  );

  // Action creators with URL updates
  const setSearch = useCallback((search: string) => {
    dispatch({ type: "SET_SEARCH", payload: search });
  }, []);

  const setCountry = useCallback((country: string) => {
    dispatch({ type: "SET_COUNTRY", payload: country });
  }, []);

  const setUserId = useCallback((userId: string) => {
    dispatch({ type: "SET_USER_ID", payload: userId });
  }, []);

  const setDateRange = useCallback((dateRange: DateRange | null) => {
    dispatch({ type: "SET_DATE_RANGE", payload: dateRange });
  }, []);

  const setSorting = useCallback((field: string, direction: "ASC" | "DESC") => {
    dispatch({ type: "SET_SORTING", payload: { field, direction } });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: "RESET_FILTERS" });
  }, []);

  // Update URL whenever filters change
  useEffect(() => {
    updateURL(filters);
  }, [filters, updateURL]);

  return {
    filters,
    actions: {
      setSearch,
      setCountry,
      setUserId,
      setDateRange,
      setSorting,
      resetFilters,
    },
  };
};
